/**
 * demo-personalize — Deterministic extraction of business signals from
 * a free-form description, then restrained substitution across the
 * demo tour.
 *
 * No LLM. No backend calls. Pure client-side parsing. Executive
 * Principle 13 + Amendment 12: personalisation should feel personally
 * relevant without becoming unpredictable.
 *
 * Patch 16 extension: the shell already substitutes the business name
 * and audience phrase. This module now derives module-level signals
 * (brand pillars, sample campaign titles, sample content titles,
 * publishing examples, morning brief bullets, workspace pill) so the
 * demo can carry the visitor's context all the way through the tour.
 *
 * All substitutions are string-only. We never fabricate numbers,
 * revenue, followers, demographics, competitors, or pricing.
 */

const DRAFT_KEY = "cz.demo.business_draft";

export interface DemoPersonalization {
  raw: string;
  hasDescription: boolean;
  /** Extracted or "Your Business". */
  businessName: string;
  /** e.g. "small-batch coffee". Lowercased. */
  categoryPhrase: string;
  /** e.g. "local cafés and remote workers". Lowercased. */
  audiencePhrase: string;
  /** A safe headline noun for the visitor's first campaign. */
  campaignNoun: string;
  /** Broad industry family, inferred from keywords. Never invented. */
  industryFamily: IndustryFamily;
  /** Short adjective describing the brand's likely tone. */
  toneWord: string;
  /** Three brand content pillars — always three, ordered. */
  pillars: [string, string, string];
  /** Three sample campaign titles the department could plan. */
  sampleCampaigns: string[];
  /** Three sample content titles (matching the campaigns). */
  sampleContentTitles: string[];
  /** Seven publishing calendar cells (Mon–Sun). Empty string == no post. */
  publishingWeek: PublishingCell[];
  /** Three morning-brief bullets — operational, never numerical. */
  morningBrief: string[];
}

export type IndustryFamily =
  | "food_beverage"
  | "retail_ecommerce"
  | "creator_media"
  | "professional_services"
  | "local_service"
  | "wellness"
  | "software"
  | "nonprofit"
  | "generic";

export interface PublishingCell {
  channel: string;
  title: string;
}

const DEFAULTS: DemoPersonalization = {
  raw: "",
  hasDescription: false,
  businessName: "Your Business",
  categoryPhrase: "your work",
  audiencePhrase: "your audience",
  campaignNoun: "Your Campaign",
  industryFamily: "generic",
  toneWord: "confident",
  pillars: ["Your story", "How the work gets made", "Your customers"],
  sampleCampaigns: [
    "Introduce Your Business",
    "Bring People Back",
    "Show the Work",
  ],
  sampleContentTitles: [
    "Meet the team behind the work",
    "Three reasons customers stay",
    "Behind the scenes this month",
  ],
  publishingWeek: [
    { channel: "IG",    title: "Story of the week" },
    { channel: "LI",    title: "Founder's note" },
    { channel: "",      title: "" },
    { channel: "EMAIL", title: "Monthly update" },
    { channel: "IG",    title: "Customer spotlight" },
    { channel: "",      title: "" },
    { channel: "TT",    title: "Behind the scenes" },
  ],
  morningBrief: [
    "Two posts ready for your review",
    "Weekly campaign brief prepared",
    "Nothing urgent on your calendar",
  ],
};

const NAME_MARKERS = [
  " specializes in ", " specialises in ",
  " helps ", " provides ", " offers ", " builds ", " creates ",
  " sells ", " runs ", " run ", " operates ", " operate ",
  " is a ", " is an ", " are a ", " are an ",
  " — ", " – ", ": ",
];

/* ── Tokenisers ────────────────────────────────────────────────── */
function firstSentence(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return "";
  const period = trimmed.indexOf(".");
  if (period === -1) return trimmed;
  return trimmed.slice(0, period);
}

function toTitleCase(s: string): string {
  return s
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => (w.length <= 3 && w !== w.toUpperCase() ? w : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(" ");
}

/* ── Extractors ─────────────────────────────────────────────── */
function extractBusinessName(sentence: string): string {
  const lower = sentence.toLowerCase();
  for (const marker of NAME_MARKERS) {
    const idx = lower.indexOf(marker);
    if (idx > 0) {
      let candidate = sentence.slice(0, idx).trim();
      candidate = candidate.replace(
        /^(we\s+at\s+|we[’']re\s+|we\s+are\s+|we\s+|our\s+company[,\s]+|our\s+business[,\s]+|our\s+team\s+at\s+|our\s+brand[,\s]+|our\s+studio[,\s]+|the\s+team\s+at\s+|i\s+run\s+|i\s+own\s+|my\s+company[,\s]+|my\s+business[,\s]+|my\s+brand[,\s]+|at\s+)/i,
        "",
      );
      // If what's left is a pronoun or a generic phrase, ignore it.
      if (candidate.length > 0 && candidate.length <= 60 && !/^(we|i|our|my|the|a|an)$/i.test(candidate)) {
        return candidate;
      }
    }
  }
  // Fallback: grab the leading capitalised phrase (up to 5 words) if the
  // description starts with what looks like a proper noun sequence.
  const cap = /^([A-Z][A-Za-z0-9&'’-]*(?:\s+[A-Z][A-Za-z0-9&'’.-]*){0,4})/.exec(sentence);
  if (cap && cap[1]) {
    // Skip if the "name" is actually a generic sentence-opener.
    const first = cap[1].split(/\s+/)[0].toLowerCase();
    const openers = new Set([
      "we","i","our","my","the","a","an","just","hi","hello",
      "small","local","ethical","boutique","family","independent",
    ]);
    if (!openers.has(first) && cap[1].length >= 3) return cap[1];
  }
  return "";
}

function extractCategory(sentence: string): string {
  const lower = sentence.toLowerCase();
  for (const marker of NAME_MARKERS) {
    const idx = lower.indexOf(marker);
    if (idx > 0) {
      const rest = sentence.slice(idx + marker.length).trim();
      if (rest) {
        const stop = rest.search(/(,|\s+for\s+|\s+in\s+)/i);
        return (stop > 0 ? rest.slice(0, stop) : rest).toLowerCase();
      }
    }
  }
  return "";
}

function extractAudience(text: string): string {
  const m = /\bfor\s+([^.]+?)(?:[.,]|$)/i.exec(text);
  if (m && m[1]) return m[1].trim().toLowerCase();
  return "";
}

/* ── Industry classifier ─────────────────────────────────────── */
const INDUSTRY_KEYWORDS: Array<[IndustryFamily, RegExp]> = [
  ["food_beverage",         /\b(coffee|roaster|cafe|café|bakery|restaurant|brewery|distillery|kitchen|food|beverage|wine|beer|chef)\b/i],
  ["retail_ecommerce",      /\b(shop|store|boutique|retail|ecommerce|e-commerce|dtc|d2c|apparel|jewelry|jewellery|goods|marketplace)\b/i],
  ["creator_media",         /\b(creator|musician|artist|author|writer|podcast|filmmaker|photographer|newsletter|blog|publication|studio)\b/i],
  ["professional_services", /\b(consult|consultant|agency|advisory|firm|law|legal|accounting|finance|financial|coach|coaching)\b/i],
  ["local_service",         /\b(lawn|landscaping|plumb|hvac|cleaning|realtor|realty|contractor|roofing|salon|barber|dental|dentist)\b/i],
  ["wellness",              /\b(fitness|wellness|yoga|pilates|therapy|therapist|nutrition|health|clinic|spa)\b/i],
  ["software",              /\b(saas|software|platform|app|api|developer|startup|b2b\s*saas)\b/i],
  ["nonprofit",             /\b(nonprofit|non-profit|charity|foundation|community|mission-driven|501\(c\))\b/i],
];

function inferIndustry(text: string): IndustryFamily {
  for (const [family, re] of INDUSTRY_KEYWORDS) {
    if (re.test(text)) return family;
  }
  return "generic";
}

/* ── Module-level derivations ────────────────────────────────── */
function toneFor(family: IndustryFamily): string {
  switch (family) {
    case "food_beverage":         return "warm";
    case "retail_ecommerce":      return "confident";
    case "creator_media":         return "editorial";
    case "professional_services": return "considered";
    case "local_service":         return "neighborly";
    case "wellness":              return "grounded";
    case "software":              return "direct";
    case "nonprofit":             return "hopeful";
    default:                      return "confident";
  }
}

function pillarsFor(family: IndustryFamily, businessName: string): [string, string, string] {
  const nm = businessName === DEFAULTS.businessName ? "your brand" : businessName;
  switch (family) {
    case "food_beverage":
      return [`Origin stories from ${nm}`, "The craft behind every batch", "Rituals & recipes"];
    case "retail_ecommerce":
      return [`Product spotlights from ${nm}`, "How it's made", "Customer moments"];
    case "creator_media":
      return [`The work of ${nm}`, "Process notes", "What's next"];
    case "professional_services":
      return ["Perspectives worth reading", "Case studies", "How we work"];
    case "local_service":
      return ["Recent projects", "Neighborhood updates", "Before & afters"];
    case "wellness":
      return ["Practice notes", "Client transformations", "What to expect"];
    case "software":
      return ["Product updates", "Customer wins", "Behind the build"];
    case "nonprofit":
      return ["Community stories", "Where support goes", "This month's mission"];
    default:
      return [`${nm} — the story`, "How the work gets made", "Your customers"];
  }
}

function sampleCampaignsFor(family: IndustryFamily, businessName: string): string[] {
  const nm = businessName === DEFAULTS.businessName ? "Your Brand" : businessName;
  switch (family) {
    case "food_beverage":
      return [`Introducing ${nm}`, "Autumn Subscribe & Save", "Home Brewer's Handbook"];
    case "retail_ecommerce":
      return [`New Arrivals from ${nm}`, "Restock & Refill", "Gift Guide"];
    case "creator_media":
      return [`${nm} — Season Launch`, "Fan Membership Drive", "Behind the Series"];
    case "professional_services":
      return [`Working with ${nm}`, "The Q4 Advisory Series", "Referral Program"];
    case "local_service":
      return [`Meet ${nm}`, "Seasonal Service Reminder", "Neighborhood Referral Drive"];
    case "wellness":
      return [`${nm} — First Visit`, "Seasonal Reset Program", "Community Class Series"];
    case "software":
      return [`${nm} — Product Launch`, "Onboarding Series", "Customer Story Feature"];
    case "nonprofit":
      return [`Meet ${nm}`, "Year-End Impact Drive", "Volunteer Recruitment"];
    default:
      return [`Introduce ${nm}`, "Bring People Back", "Show the Work"];
  }
}

function sampleContentTitlesFor(family: IndustryFamily, businessName: string): string[] {
  const nm = businessName === DEFAULTS.businessName ? "your brand" : businessName;
  switch (family) {
    case "food_beverage":
      return [
        `The origin behind ${nm}'s newest release`,
        "Why we chose this lot",
        "How to brew it at home",
      ];
    case "retail_ecommerce":
      return [
        `What's new at ${nm} this week`,
        "How it's made",
        "Customer spotlight",
      ];
    case "creator_media":
      return [
        `${nm} — the story behind the work`,
        "Process notes from this season",
        "What's next",
      ];
    case "professional_services":
      return [
        `A note from ${nm}`,
        "How we work with clients",
        "Case study of the month",
      ];
    case "local_service":
      return [
        `Recent projects from ${nm}`,
        "What good service looks like",
        "Before & after",
      ];
    case "wellness":
      return [
        `A welcome from ${nm}`,
        "What to expect at your first visit",
        "Client story",
      ];
    case "software":
      return [
        `What shipped at ${nm} this month`,
        "How teams use us",
        "Behind the roadmap",
      ];
    case "nonprofit":
      return [
        `A message from ${nm}`,
        "Where your support goes",
        "This month in the community",
      ];
    default:
      return [
        `Meet the team behind ${nm}`,
        "Three reasons customers stay",
        "Behind the scenes this month",
      ];
  }
}

function publishingWeekFor(family: IndustryFamily, businessName: string): PublishingCell[] {
  const nm = businessName === DEFAULTS.businessName ? "your brand" : businessName;
  // Deterministic 7-day pattern. Blank cells stay blank.
  const empty: PublishingCell = { channel: "", title: "" };
  switch (family) {
    case "food_beverage":
      return [
        { channel: "IG",    title: `Origin post — ${nm}` },
        { channel: "LI",    title: "Founder's note" },
        { channel: "IG",    title: "Brew guide reel" },
        { channel: "EMAIL", title: "Subscribe drop" },
        { channel: "IG",    title: "Customer spotlight" },
        empty,
        { channel: "TT",    title: "Roasting BTS" },
      ];
    case "retail_ecommerce":
      return [
        { channel: "IG",    title: `New at ${nm}` },
        empty,
        { channel: "IG",    title: "How it's made" },
        { channel: "EMAIL", title: "Restock alert" },
        { channel: "LI",    title: "Founder's note" },
        empty,
        { channel: "TT",    title: "Customer unboxing" },
      ];
    case "creator_media":
      return [
        { channel: "IG",    title: "Process notes" },
        { channel: "LI",    title: `A note from ${nm}` },
        empty,
        { channel: "EMAIL", title: "Newsletter drop" },
        { channel: "IG",    title: "New work reveal" },
        empty,
        { channel: "TT",    title: "Behind the scenes" },
      ];
    case "professional_services":
      return [
        { channel: "LI",    title: "Perspective of the week" },
        empty,
        { channel: "LI",    title: "Case study spotlight" },
        { channel: "EMAIL", title: "Advisory digest" },
        { channel: "LI",    title: "Client story" },
        empty,
        empty,
      ];
    case "local_service":
      return [
        { channel: "IG",    title: "Recent project" },
        { channel: "IG",    title: "Neighborhood tip" },
        empty,
        { channel: "EMAIL", title: "Seasonal reminder" },
        { channel: "IG",    title: "Before & after" },
        empty,
        empty,
      ];
    case "wellness":
      return [
        { channel: "IG",    title: "Practice note" },
        empty,
        { channel: "IG",    title: "Client story" },
        { channel: "EMAIL", title: "Weekly reflection" },
        { channel: "IG",    title: "Class schedule" },
        empty,
        empty,
      ];
    case "software":
      return [
        { channel: "LI",    title: "Product update" },
        { channel: "TW",    title: "Feature preview" },
        empty,
        { channel: "EMAIL", title: "Release notes" },
        { channel: "LI",    title: "Customer story" },
        empty,
        empty,
      ];
    case "nonprofit":
      return [
        { channel: "IG",    title: "Community story" },
        empty,
        { channel: "IG",    title: "Volunteer spotlight" },
        { channel: "EMAIL", title: "Monthly update" },
        { channel: "IG",    title: "Where support goes" },
        empty,
        empty,
      ];
    default:
      return DEFAULTS.publishingWeek;
  }
}

function morningBriefFor(family: IndustryFamily, businessName: string): string[] {
  const nm = businessName === DEFAULTS.businessName ? "your brand" : businessName;
  const shared = [
    `Weekly campaign brief prepared for ${nm}`,
    "Two posts ready for your review",
    "Nothing urgent on your calendar",
  ];
  switch (family) {
    case "food_beverage":
      return [
        "Origin story post ready for your review",
        `Autumn Subscribe & Save prepared for ${nm}`,
        "Editorial team drafted this week's brew guide",
      ];
    case "retail_ecommerce":
      return [
        "New arrivals lineup drafted for review",
        `Restock reminder prepared for ${nm}`,
        "Product spotlight scheduled pending approval",
      ];
    case "creator_media":
      return [
        "Season launch teaser drafted for your review",
        `Newsletter prepared for ${nm}`,
        "Two social posts scheduled pending approval",
      ];
    case "professional_services":
      return [
        "Perspective post drafted for your review",
        `Advisory digest prepared for ${nm}`,
        "Case study scheduled pending approval",
      ];
    case "local_service":
      return [
        "Recent project post drafted for your review",
        `Seasonal reminder prepared for ${nm}`,
        "Neighborhood tip scheduled pending approval",
      ];
    case "wellness":
      return [
        "Weekly practice note drafted for review",
        `Class schedule prepared for ${nm}`,
        "Client story ready to publish pending approval",
      ];
    case "software":
      return [
        "Release notes drafted for your review",
        `Onboarding email prepared for ${nm}`,
        "Customer story scheduled pending approval",
      ];
    case "nonprofit":
      return [
        "Community story drafted for your review",
        `Monthly update prepared for ${nm}`,
        "Volunteer spotlight scheduled pending approval",
      ];
    default:
      return shared;
  }
}

function makeCampaignNoun(business: string, category: string): string {
  if (!business && !category) return DEFAULTS.campaignNoun;
  const noun = category
    ? category
      .split(/\s+/)
      .slice(0, 3)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ")
    : business.split(/\s+/).slice(0, 2).join(" ");
  return `${noun} Awareness Campaign`;
}

/* ── Public API ─────────────────────────────────────────────── */
export function personalizeFromDraft(description: string | null | undefined): DemoPersonalization {
  const raw = (description ?? "").trim();
  if (!raw) return DEFAULTS;

  const sentence = firstSentence(raw);
  const rawName = extractBusinessName(sentence);
  const businessName = rawName ? toTitleCase(rawName) : DEFAULTS.businessName;
  const category = extractCategory(sentence) || DEFAULTS.categoryPhrase;
  const audience = extractAudience(raw) || DEFAULTS.audiencePhrase;
  const campaignNoun = makeCampaignNoun(
    businessName === DEFAULTS.businessName ? "" : businessName,
    category === DEFAULTS.categoryPhrase ? "" : category,
  );
  const industryFamily = inferIndustry(raw);
  const toneWord = toneFor(industryFamily);
  const pillars = pillarsFor(industryFamily, businessName);
  const sampleCampaigns = sampleCampaignsFor(industryFamily, businessName);
  const sampleContentTitles = sampleContentTitlesFor(industryFamily, businessName);
  const publishingWeek = publishingWeekFor(industryFamily, businessName);
  const morningBrief = morningBriefFor(industryFamily, businessName);

  return {
    raw,
    hasDescription: true,
    businessName,
    categoryPhrase: category,
    audiencePhrase: audience,
    campaignNoun,
    industryFamily,
    toneWord,
    pillars,
    sampleCampaigns,
    sampleContentTitles,
    publishingWeek,
    morningBrief,
  };
}

export function readDemoDraft(): string {
  try { return window.localStorage.getItem(DRAFT_KEY) ?? ""; } catch { return ""; }
}

export function writeDemoDraft(text: string): void {
  try { window.localStorage.setItem(DRAFT_KEY, text); } catch { /* noop */ }
}

export function clearDemoDraft(): void {
  try { window.localStorage.removeItem(DRAFT_KEY); } catch { /* noop */ }
}

export const DEMO_DRAFT_KEY = DRAFT_KEY;
